import React from 'react';
import { ACTIONS_TYPES } from 'redux/helper';
import { Notification } from 'models';
import {
  NOTIFICATION_TYPE, DEVICE_TYPE, ROUTE, RESOURCE_TYPE, STORAGE_TYPE, CPU_FAMILY,
} from 'constant';
import { getDispatch } from 'redux/store';
import { getAssets } from 'themes/assets';
import { _t } from './translation';
import { reFetchAndDispatchItem } from './provisioning';
import { getConfig } from './config';

const assets = getAssets();

export const getParam = (name) => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has(name)) {
    return urlParams.get(name);
  }
  return null;
};

export const getDeviceType = () => {
  const breakPoints = [
    {
      type: DEVICE_TYPE.MOBILE,
      minWidth: 0,
      maxWidth: 600,
    },
    {
      type: DEVICE_TYPE.TABLET,
      minWidth: 601,
      maxWidth: 992,
    },
    {
      type: DEVICE_TYPE.DESKTOP,
      minWidth: 993,
      maxWidth: 4000,
    },
  ];

  const width = (window.innerWidth > 0) ? window.innerWidth : window.screen.width;
  const device = breakPoints
    .find(({ minWidth, maxWidth }) => width >= minWidth && width <= maxWidth);
  return device ? device.type : DEVICE_TYPE.DESKTOP;
};

export const uniqueArray = (array) => Array.from(new Set(array));

export const getLanColor = (id) => {
  const lanPalette = [
    '#009688', '#FF5722', '#ed008c', '#9E9E9E', '#795548', '#8BC34A',
    '#00BCD4', '#F44336', '#9C27B0', '#4CAF50', '#cddc39', '#435719',
    '#FFEB3B', '#3F51B5', '#2196F3', '#4de4e6', '#f50104', '#d7b383',
  ];

  return lanPalette[(id % lanPalette.length) - 1];
};

export const percent = (value, total) => {
  if (!value || !total) return 0;
  let percentage = ((parseFloat(value) / parseFloat(total)) * 100);
  percentage = Number.isNaN(percentage) ? 0 : percentage;
  return percentage === Infinity ? 0 : percentage;
};

export const resourceIconMap = (name) => {
  const icons = {
    [RESOURCE_TYPE.DATA_CENTER]: 'boxes',
    [RESOURCE_TYPE.SNAPSHOT]: 'camera-retro',
    [RESOURCE_TYPE.IMAGE]: 'compact-disc',
    [RESOURCE_TYPE.IP_BLOCK]: 'network-wired',
    [RESOURCE_TYPE.K8S]: 'asterisk',
    [RESOURCE_TYPE.SERVER]: 'server',
    [RESOURCE_TYPE.HDD]: 'hdd',
    [RESOURCE_TYPE.SSD]: 'sd-card',
    [RESOURCE_TYPE.GROUP]: 'users',
    [RESOURCE_TYPE.RESOURCE]: 'object-group',
    [RESOURCE_TYPE.LAN]: 'ethernet',
    [RESOURCE_TYPE.VOLUME]: 'hdd',
    [RESOURCE_TYPE.LOAD_BALANCER]: 'sitemap',
    [RESOURCE_TYPE.NIC]: 'ethernet',
    [RESOURCE_TYPE.FIREWALL_RULE]: 'fire-alt',
    [RESOURCE_TYPE.BACKUP_UNIT]: 'layer-group',
    [RESOURCE_TYPE.S3KEY]: 'key',
    [RESOURCE_TYPE.FORWARDING_RULE]: 'angle-double-right',
    [RESOURCE_TYPE.NETWORK_LOAD_BALANCER]: 'sitemap',
    [RESOURCE_TYPE.APPLICATION_LOAD_BALANCER]: 'sitemap',
    [RESOURCE_TYPE.FLOW_LOG]: 'exchange-alt',
    [RESOURCE_TYPE.ACTION]: 'paper-plane',
    [RESOURCE_TYPE.NATGATEWAY]: 'network-wired',
    [RESOURCE_TYPE.NATGATEWAY_RULE]: 'angle-double-right',
    [RESOURCE_TYPE.TARGET_GROUP]: 'bullseye',
    [RESOURCE_TYPE.ALB_FORWARDING_RULE]: 'angle-double-right',
  };

  return icons[name] || name;
};

export const sleep = (ms, cb = null) => new Promise((resolve) => setTimeout(() => {
  if (cb) {
    cb();
  }
  resolve();
}, ms));

export const locations = {
  'de/fra': {
    image: assets.DE,
    name: _t('Germany / Frankfurt am Main'),
  },
  'de/txl': {
    image: assets.DE,
    name: _t('Germany / Berlin'),
  },
  'us/ewr': {
    image: assets.US,
    name: _t('USA / Newark'),
  },
  'de/fkb': {
    image: assets.DE,
    name: _t('Germany / Karlsruhe'),
  },
  'us/las': {
    image: assets.US,
    name: _t('USA / Las Vegas'),
  },
  'us/mci': {
    image: assets.US,
    name: _t('USA / Lenexa'),
  },
  'gb/lhr': {
    image: assets.UK,
    name: _t('United Kingdom / London'),
  },
  'gb/bhx': {
    image: assets.UK,
    name: _t('United Kingdom / Worcester'),
  },
  'kr/icn': {
    image: assets.KR,
    name: _t('South Korea / Seoul'),
  },
  'es/vit': {
    image: assets.ES,
    name: 'Spain / Logroño',
  },
  'fr/par': {
    image: assets.FR,
    name: 'France / Paris',
  },
  'ag/s01': {
    image: assets.DE,
    name: 'ag/s01',
  },
  'ag/s02': {
    image: assets.DE,
    name: 'ag/s02',
  },
  'ag/s03': {
    image: assets.DE,
    name: 'ag/s03',
  },
  'ag/p01': {
    image: assets.DE,
    name: 'ag/p01',
  },
  'ag/p02': {
    image: assets.DE,
    name: 'ag/p02',
  },
  'ag/p03': {
    image: assets.DE,
    name: 'ag/p03',
  },
};

export const dispatchPendingAction = ({ action }) => {
  const dispatch = getDispatch();

  return dispatch({
    type: ACTIONS_TYPES.COMMON.PENDING_ACTION,
    payload: { action },
  });
};

export const removePendingActions = () => {
  const dispatch = getDispatch();

  return dispatch({
    type: ACTIONS_TYPES.COMMON.REMOVE_PENDING_ACTION,
    payload: {},
  });
};

export const hotGetAction = async ({
  action,
  apiResult,
  payloadKey,
  expectedStatusCodes,
  isRetrieveItems = false,
  retrieveByFunction = null,
  isPassiveLoading = false,
}) => {
  if (!isPassiveLoading) {
    dispatchPendingAction({
      action,
    });
  }

  const response = await apiResult;
  const { httpStatus = null, messages = [] } = response || {};
  if (httpStatus && (!expectedStatusCodes || !expectedStatusCodes.includes(httpStatus))) {
    const [message = {}] = messages;
    const dispatch = getDispatch();

    dispatch({
      type: ACTIONS_TYPES.COMMON.QUICK_UPDATE,
      payload: {
        notifications: [
          Notification({
            type: NOTIFICATION_TYPE.ERROR,
            action,
            message: message.message
              ? message.message
              : _t('Something went wrong!'),
          }),
        ],
      },
    });
  } else {
    let result = response;
    if (isRetrieveItems) {
      result = response.items;
    }

    if (retrieveByFunction) {
      result = retrieveByFunction(response);
    }

    const dispatch = getDispatch();

    dispatch({
      type: action,
      payload: { [payloadKey || action]: result },
    });
  }
};

export const basicCRUDAction = async ({
  action,
  apiResult,
  successMessage,
  isReFetch = true,
  reFetchTime = 2000,
  isPassiveLoading = false,
  isAddResponseToStore = false,
}) => {
  if (!isPassiveLoading) dispatchPendingAction({ action });
  const response = await apiResult;
  const { httpStatus = null, messages = [] } = response;
  const dispatch = getDispatch();

  if (httpStatus) {
    const [message = {}] = messages;

    dispatch({
      type: ACTIONS_TYPES.COMMON.QUICK_UPDATE,
      payload: {
        notifications: [
          Notification({
            type: NOTIFICATION_TYPE.ERROR,
            action,
            message: message.message
              ? message.message
              : _t('Something went wrong!'),
          }),
        ],
        pendingActions: [],
      },
    });
  } else {
    dispatch({ type: action, payload: response, successMessage });
    if (isAddResponseToStore) dispatch({ type: ACTIONS_TYPES.COMMON.QUICK_UPDATE, payload: { [action]: response } });
    if (isReFetch) sleep(reFetchTime).then(() => reFetchAndDispatchItem());
  }
  return response;
};

export const flattenArray = (arrays) => [].concat(...arrays);

export const extractDatacenterId = (url = '') => {
  const [, id = null] = (url || '').match('.+/datacenters/(.+?)/.+') || [];
  return id;
};

export const extractServerId = (url = '', exactUrl = false) => {
  const [, id = null] = url.match(exactUrl ? '.+/servers/(.+)' : '.+/servers/(.+?)/.+') || [];
  return id;
};

export const extractDatacenterUrl = (url = '') => {
  const [, dcUrl = null] = (url || '').match('(.+/datacenters/.+?)/.+') || [];
  return dcUrl;
};

export const hasKeys = (obj) => !!Object.keys(obj || {}).length;

export const cleanEmptyProperty = (object) => {
  for (const prop in object) {
    if (!object[prop]) {
      delete object[prop];
    }
  }
  return object;
};

export const varToText = (name) => {
  let newName = name.replace(/[A-Z]/g, (e) => ` ${e}`);
  newName = newName.replace(/^\S/, (e) => e.toUpperCase());
  return newName;
};

export const capitalize = (str = '') => str.replace(/\S/g, (e) => e.toLowerCase()).replace(/^\S/, (e) => e.toUpperCase());

export const uppercase = (str = '') => str.replace(/\S/g, (e) => e.toUpperCase());

export const uppercaseUnderscore = (str = '') => str.replace(/\S/g, (e) => e.toUpperCase()).replaceAll('-', '_');

export const objSafeProps = (obj) => {
  if (typeof obj !== 'object') return obj;

  for (const key in obj) {
    if (obj[key] === undefined) delete obj[key];
  }

  return obj;
};

export const objectToArray = (object) => {
  const keys = Object.keys(object);
  return keys.map((e) => [varToText(e), typeof object[e] === 'string' ? object[e] : JSON.stringify(object[e], null, 4)]);
};

export const objectToOptions = (object, exceptKeys = []) => {
  let keys = Object.keys(object);
  keys = keys.filter((e) => !exceptKeys.includes(object[e]));
  return keys.map((e) => [e, object[e]]);
};

export const formatNumber = (num) => Number(num).toLocaleString();

export const replaceObjectProps = (target = {}, source = {}) => {
  if (!source || typeof source !== 'object' || typeof target !== 'object') return target;
  const targetKeys = Object.keys(target);
  const result = {};
  for (const key of targetKeys) {
    result[key] = source[key] ? source[key] : target[key];
  }
  return result;
};

export const downloadFile = ({ content, type, filename }) => {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = `data:${type},${content}`;

  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const isObjectEqual = (sourceObj = {}, targetObj = {}) => {
  const sourceKeys = Object.keys(sourceObj);
  const targetKeys = Object.keys(targetObj);
  if (sourceKeys.length !== targetKeys.length) return false;

  for (const key of sourceKeys) {
    if (sourceObj[key] !== targetObj[key]) return false;
  }

  return true;
};

export const setKey = (name, value = {}) => (
  window.localStorage.setItem(name, JSON.stringify(value))
);

export const getKey = (name) => (
  JSON.parse(window.localStorage.getItem(name)) || ''
);

export const clearKey = (name) => (
  window.localStorage.removeItem(name)
);

export const setSessionKey = (name, value = {}) => (
  window.sessionStorage.setItem(name, JSON.stringify(value))
);

export const getSessionKey = (name) => (
  JSON.parse(window.sessionStorage.getItem(name)) || ''
);

export const clearSessionKey = (name) => (
  window.sessionStorage.removeItem(name)
);

export const setToken = ({ token, type = '' }) => {
  if (type === 'url') {
    window.location.assign(`#token=${token}`);
  } else if (type === 'jwt') {
    setSessionKey('jwtToken', token);
  } else {
    setSessionKey('token', token);
  }
};

export const clearToken = () => {
  clearSessionKey('token');
  clearSessionKey('jwtToken');
};

export const getUnattachedVolumes = (dataCenter = {}) => {
  const { entities = {} } = dataCenter;
  if (!hasKeys(entities)) return [];

  const {
    servers: {
      items: serverItems,
    },
    volumes: {
      items: allVolumes,
    },
  } = entities;

  const serverVolumes = flattenArray(
    serverItems
      .map(({
        entities: {
          volumes: {
            items = [],
          },
        },
      }) => items),
  );
  const serverVolumeIds = serverVolumes.map(({ id }) => id);
  return allVolumes.filter(({ id }) => !serverVolumeIds.includes(id));
};

export const enumToArray = (enums, labels) => (
  Object.keys(enums).map((e) => (labels ? [enums[e], labels[e] || enums[e]] : enums[e]))
);

export const getCurrentMenu = () => (
  enumToArray(ROUTE.DASHBOARD)
    .find(({ pathRegex = /(?:)/ }) => pathRegex.test(window.location.hash))
);

export const getBreadcrumbPaths = () => {
  const { pathname } = window.location;
  const routes = Object.keys(ROUTE.DASHBOARD).map((e) => ROUTE.DASHBOARD[e]);
  const route = routes.find(({ pathRegex }) => pathname.match(pathRegex));
  return route && route.breadcrumb ? route.breadcrumb() : null;
};

export const getToken = (type = '') => {
  if (type === 'url') {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const [_token = ''] = hash.split('&');
      const [, token = null] = _token.split('=');
      return token;
    }
  } else if (type === 'jwt') {
    return getSessionKey('jwtToken');
  } else {
    return getSessionKey('token');
  }
  return null;
};

export const getMyEmail = () => {
  const token = window.atob(getToken());
  let [email = ''] = token.split(':');
  if (!email) {
    email = getSessionKey('userMail');
  }
  return email;
};

export const shortText = (txt, length) => (txt.length > length ? `${txt.substr(0, length)}...` : txt);

export const copyToClipboard = (str) => {
  const fallback = () => {
    const el = document.createElement('textarea');
    el.value = str;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    el.style.zIndex = '9999';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };
  try {
    if (navigator && navigator.clipboard) { // eslint-disable-line
      navigator.clipboard.writeText(str); // eslint-disable-line
    } else if (window.clipboardData && window.clipboardData.setData) { // eslint-disable-line
      window.clipboardData.setData('Text', str); // Internet Explorer 11 and below
    } else {
      fallback();
    }
  } catch (e) {
    fallback();
  }
};

export const localCurrency = (no, unit) => {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: unit || 'USD',
  });

  return formatter.format(no);
};

export const getServerId = (url) => url.match(/.+?servers\/(.+)/)[1];

export const generateServerObjUrl = ({ dcId, serverId }) => {
  const { apiBaseUrl = '' } = getConfig() || {};
  return `${apiBaseUrl}/datacenters/${dcId}/servers/${serverId}`;
};

export const generateDatacenterObjUrl = ({ dcId }) => {
  const { apiBaseUrl = '' } = getConfig() || {};
  return `${apiBaseUrl}/datacenters/${dcId}`;
};

export const appendRemoteScript = ({
  url = '',
  type = 'script',
  id = 'script',
}) => new Promise((resolve) => {
  let script;

  if (type === 'script') {
    script = document.createElement('script');
    script.src = url;
  } else if (type === 'style') {
    script = document.createElement('link');
    script.href = url;
  }

  script.id = id;
  script.onload = () => {
    resolve(true);
  };

  return document.body.append(script);
});

export const copyObject = (input) => (JSON.parse(JSON.stringify(input)));

/**
 * naive compare of two DCD versions
 * given the standard versioning convention of
 * number.number.number (Major.Minor.Patch)
 * @param {string} current i.e. 3.2.0
 * @param {string} expected i.e. 3.2.1
 * @return {boolean} true = current >= expected | false = current < expected
 */
export const compareDCDVersions = (current, expected) => {
  const ver1 = current.split('.');
  const ver2 = expected.split('.');

  for (let i = 0, il = ver2.length; i < il; i++) {
    const v1 = ver1[i] * 1;
    const v2 = ver2[i] * 1;
    if (v1 < v2) { return false; }
  }

  return true;
};
export const isEmptyArray = (arr) => !Array.isArray(arr) || !arr.length;

export const isEmptyObject = (obj) => !obj || !Object.keys(obj).length;

export const isNullOrUndefined = (value) => (value === null || value === undefined);

export const createLink = (route, params) => {
  let { path = '' } = typeof route === 'string' ? { path: route } : route;
  path = path.replace(/\+/g, '');
  // eslint-disable-next-line guard-for-in
  for (const param in params) path = path.replace(`:${param}`, params[param]);
  return path;
};


export const computeTotalHDDandSSD = (items) => (items || []).reduce((t, e) => {
  const { properties: volumeProps = {} } = e;
  const { size, type } = volumeProps;
  if (type === STORAGE_TYPE.HDD) {
    t.hdd.push(size);
  } else if (type === STORAGE_TYPE.SSD || type === STORAGE_TYPE['SSD Standard'] || type === 'SSD Premium') {
    t.ssd.push(size);
  }
  return t;
}, { ssd: [], hdd: [] });

export const explorerObj = (obj, key) => (
  key.split('.').reduce((screen, e) => screen[e], obj)
);

export const toCurrency = (no) => Math.abs(parseFloat(no)).toFixed(2);

export const getCPUFamilyOptions = (cpuArchitectures) => cpuArchitectures.map(({ cpuFamily: cpuFamilyName }) => [
  cpuFamilyName,
  <span>
    <img
      src={CPU_FAMILY[cpuFamilyName].logo}
      style={{ height: 18, marginRight: 10, verticalAlign: 'middle' }}
      alt={CPU_FAMILY[cpuFamilyName].name}
    />
    {CPU_FAMILY[cpuFamilyName].name}
  </span>,
]);

export const calculateMinSizeForImage = (imageOrSnap, puffer) => {
  if (!imageOrSnap) return 1;
  const imageSize = imageOrSnap.size;

  if (imageOrSnap.type === 'vsnapshot') {
    return Math.ceil(imageSize);
  }

  // "Ceiled" ImageSize in GB + 2 GB
  return Math.ceil((imageSize) + puffer);
};

export const getCompatibleVersions = ({
  versions, currentVersion, onlyHigherVersions = false, sameVersion = true,
}) => {
  const hasVersionMarker = currentVersion.startsWith('v');
  const [major, minor, patch] = currentVersion.substr(hasVersionMarker ? 1 : 0).split('.').map((item) => Number(item));

  return versions.map((version) => {
    const versionMarker = version.startsWith('v');
    const [innerMajor, innerMinor, innerPatch] = version.substr(versionMarker ? 1 : 0).split('.').map((item) => Number(item));
    return {
      major: innerMajor, minor: innerMinor, patch: innerPatch,
    };
  }).reduce((acc, version) => {
    if (onlyHigherVersions ? major < version.major : major > version.major) {
      // PUSH
      acc = [...acc, version];
    } else if (major === version.major) {
      if (onlyHigherVersions ? minor < version.minor : minor > version.minor) {
        acc = [...acc, version];
      } else if (minor === version.minor
          && (Number.isNaN(version.patch)
          || (onlyHigherVersions ? patch < version.patch : patch > version.patch)
          || (patch === version.patch && sameVersion))) {
        acc = [...acc, version];
      }
    }
    return acc;
  }, [])
    .map((version) => (`${hasVersionMarker ? 'v' : ''}${version.major}.${version.minor}${version.patch !== undefined ? `.${version.patch}` : ''}`));
};

export const formatFileSizeBinary = (fileSize, round) => {
  const units = ['b', 'kB', 'MB', 'GB', 'TB'];
  let size = Math.abs(fileSize);

  if (Number.isNaN(size)) {
    return 'Invalid file size';
  }

  if (size === 0) {
    return '0 bytes';
  }

  let quotient = Math.floor(Math.log2(size) / 10);
  quotient = quotient < units.length ? quotient : units.length - 1;

  size /= (1024 ** quotient);

  if (round) {
    if (quotient === 2) return `${+(size).toFixed(0)} ${units[quotient]}`;
    if (quotient >= 3) return `${+(Math.round(size * 10) / 10).toFixed(2)} ${units[quotient]}`;
  }

  return `${+size.toFixed(2)} ${units[quotient]}`;
};

export const getFormatFileSizeFromByte = (filesize, unit) => {
  const units = ['b', 'kB', 'MB', 'GB', 'TB'];
  const multiplier = units.indexOf(unit);
  return (Math.round((filesize / 1024 ** multiplier) * 100) / 100).toFixed(2);
};

export const formatNumberSize = (value) => {
  let x = value;
  let suffix = '';
  if (x > 10000) {
    suffix = 'k';
    x = Math.round(x / 1000);
  } else if (x > 1000) {
    suffix = 'k';
    x = Math.round(x / 100) / 10;
  }

  return `${x}${suffix}`;
};

export const toInt = (no, defaultValue = 0) => parseInt(no || defaultValue, 10) || defaultValue;

export const objectExistsInArray = (object, list) => (
  list.find((item) => (Object.keys(object).every((property) => (
    item[property] === object[property]
  ))))
);

export const getErrorMessages = (errors) => {
  try {
    const messages = [];
    if (errors) {
      if (Array.isArray(errors)) {
        errors.forEach((error) => {
          const { message, detail, code } = error;
          if (error.message) {
            const detailString = Object.keys(detail || {}).map((key) => `${key}: ${detail[key]}`).join(', ');
            messages.push({ message: `${code ? `${code} ` : ''}${message}${detail ? ` (${detailString})` : ''}` });
          }
        });
      } else {
        Object.keys(errors)
          .forEach((key) => {
            if (errors[key]) {
              messages.push({ message: errors[key] });
            }
          });
      }
    }
    return messages.length > 0 ? messages : false;
  } catch (err) {
    return false;
  }
};

export const isSsdStorageType = (value) => value.toLowerCase().startsWith('ssd');
export const isHddStorageType = (value) => value.toLowerCase().startsWith('hdd');
